/**
 * Auth0-style org/tenant selector for the top bar.
 *
 * The trigger shows the active region flag + org / tenant; the menu mirrors
 * Auth0's account switcher:
 *
 *  - **Switch tenant** — silent token refresh via `SessionManager.switchTenant`;
 *  - **Create tenant** — an org-admin provisioning action (POST `/tenants`),
 *    gated on the `users:create` permission AND the plan's `tenancy_creation`
 *    entitlement (hidden, not upsold, without it), behind a small dialog;
 *  - **Invite people** — routes to the Team surface (the real invite + roles UI);
 *  - links to the members & settings surfaces.
 *
 * Region flag/labels come from the shared `@authe/fe-commons/region` vocabulary
 * so every app reads the same map.
 */
import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Check, ChevronsUpDown, Plus, Users, UserPlus, Network, Settings as SettingsIcon } from "lucide-react";
import { roleLabelOf } from "@authe/fe-commons/auth";
import { useSession } from "@authe/ui-core/session";
import { useCurrentOrg } from "@authe/ui-core/tenant";
import { usePermissions } from "@authe/ui-core/permissions";
import { CreateTenantDialog } from "@/components/CreateTenantDialog";
import { useCanCreateTenancy } from "@/components/tenancyCreation";
import {
  AWS_REGION_ALIASES,
  AWS_REGIONS,
  CONTINENT_FLAG,
  CONTINENT_SHORT_LABEL,
  continentForRegionOrAlias,
} from "@authe/fe-commons/region";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  notifyError,
  useEntitled,
} from "@authe/ui-core";
import { useTenancySource } from "@/api/adapters";
import type { MyOrganization, TenantSummary } from "@/api/tenancySource";
import { ENTITLEMENT } from "@authe/fe-commons/plans";
import { Hint } from "./Hint";
import { appConfig, ROUTES, RESOURCES } from "@/config";

export function OrgTenantMenu(): React.ReactElement {
  const { manager, session } = useSession();
  /**
   * An impersonated session cannot finish a switch.
   *
   * Switching is two steps: the server rewrites the active tenancy, and the app
   * RE-AUTHENTICATES to get a token that says so. `ImpersonationClient
   * .loginWithRedirect` refuses by design (redirecting would authenticate the
   * operator at the keyboard), so step two always fails here — and step one had
   * already moved the customer. The operator was then told "you are still in the
   * current one" about a switch that had in fact been saved, and the customer's
   * next login landed somewhere nobody chose.
   */
  const impersonating = Boolean(session?.claims?.impersonated);
  const org = useCurrentOrg();
  const { can } = usePermissions();
  // Being multi-tenant is a PACKAGED capability, distinct from being permitted
  // to administer tenants. Granted on every tier today, so this changes nothing
  // yet — but when it is withheld the switcher, the create action and the link
  // to the tenancy page have to go together, or the reader finds a door to a
  // page that is no longer in the rail.
  const entitled = useEntitled();
  const multiTenant = entitled(ENTITLEMENT.TENANCY_SWITCHING);
  // Making MORE tenants is its own packaged capability, narrower than being
  // multi-tenant: a plan may keep the tenants it has without adding any.
  const tenancyCreationIncluded = useCanCreateTenancy();
  const tenancy = useTenancySource();
  const [busy, setBusy] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  // Only the active tenant's friendly name is in the token; resolve the rest
  // (best-effort) from management-api the first time the menu opens.
  const [nameById, setNameById] = React.useState<Record<string, string>>({});
  const [namesLoaded, setNamesLoaded] = React.useState(false);
  // The caller's OWN memberships — a different question from "organizations
  // under my customer". Accepting an invitation to a second org is what puts a
  // row here, and until this list existed that invitation had nowhere to show.
  const [myOrgs, setMyOrgs] = React.useState<MyOrganization[]>([]);
  // The org's tenants, from the API. This used to read a `tenant_ids` / `tenants`
  // ID-token claim that the post-login Action does not emit, so the list was
  // always empty and the switcher never rendered at all.
  const [tenantRows, setTenantRows] = React.useState<TenantSummary[]>([]);
  /**
   * Whether the two reads behind this menu failed.
   *
   * Both used to be swallowed by empty `catch` blocks, and `tenantRows` then
   * stayed `[]` — which the render read as "this org has one tenant" and drew
   * no switcher at all. On a multi-tenant product that is the worst available
   * outcome: the operator concludes they have nowhere else to go, so they never
   * discover that the tenant holding the case they are looking for exists. Same
   * for the organization list, which is what an accepted second-org invitation
   * shows up in.
   */
  const [tenantsError, setTenantsError] = React.useState<unknown>(null);
  const [orgsError, setOrgsError] = React.useState<unknown>(null);
  const [reloading, setReloading] = React.useState(false);

  const current = org.tenantId ?? null;
  // Only the tenants the caller holds a role in (permissions' my-organizations).
  // Every tenant of the org used to be offered, and a switch into one they hold
  // nothing in is refused ("You hold no role in that tenant"), so a desk member
  // saw entries that could only fail. Unknown (an older API) hides nothing.
  const grantsHere = myOrgs.find((o) => o.orgId === org.orgId)?.tenants ?? null;
  const reachable = grantsHere ? new Set(grantsHere.map((t) => t.tenantId)) : null;
  const allTenants = tenantRows.map((t) => t.id);
  const tenants = reachable ? allTenants.filter((t) => reachable.has(t) || t === current) : allTenants;
  const hiddenTenants = allTenants.length - tenants.length;
  const rolesIn = (tenantId: string): string =>
    (grantsHere?.find((t) => t.tenantId === tenantId)?.roles ?? []).join(", ");
  // Memberships carry current display names, including for impersonated tokens
  // that only carry internal role names such as sandbox-Reader.
  const currentRoles = grantsHere?.find((t) => t.tenantId === current)?.roles
    ?? session?.claims.roles?.map((role) => roleLabelOf(session.claims, role)) ?? [];
  // The tenant list is drawn below the org list; when both are, the
  // impersonation note goes once, after the last one.
  const listsTenants = tenantsError == null && multiTenant && tenants.length > 1;

  const loadNames = React.useCallback(
    async (force = false) => {
      if (!org.orgId) return;
      if (namesLoaded && !force) return;
      setNamesLoaded(true);
      setReloading(true);
      try {
        const rows = await tenancy.listTenants(org.orgId);
        setTenantRows(rows);
        // Only REAL names are indexed. Storing `r.name || r.id` meant an
        // unnamed tenant's id became its "name", and the switcher then offered
        // `ten_7f3a…` as a label — unreadable, and indistinguishable from a
        // tenant actually called that.
        setNameById(
          Object.fromEntries(
            rows.filter((r) => Boolean(r.name)).map((r) => [r.id, r.name as string]),
          ),
        );
        setTenantsError(null);
      } catch (error) {
        // Kept, not swallowed. The menu below says the list is unknown and
        // offers a retry rather than implying there is nothing to switch to.
        setTenantsError(error ?? new Error("Could not load this organization's tenants"));
      }
      try {
        setMyOrgs(await tenancy.listMyOrganizations());
        setOrgsError(null);
      } catch (error) {
        setOrgsError(error ?? new Error("Could not load your organizations"));
      }
      setReloading(false);
    },
    [namesLoaded, org.orgId, tenancy],
  );

  /**
   * A tenant's display name, or a labelled id.
   *
   * Never a bare id: a raw `ten_7f3a…` in a switcher is unreadable, and
   * indistinguishable from a tenant actually NAMED that. When the name read
   * failed, the id is shown as what it is.
   */
  const tenantLabel = (id: string): string => {
    const known = nameById[id];
    if (known) return known;
    if (id === current) return org.tenantName;
    return `Unnamed tenant (${id})`;
  };

  const continent = continentForRegionOrAlias(appConfig.region);
  const fullRegion = AWS_REGIONS[AWS_REGION_ALIASES[appConfig.region] ?? appConfig.region];

  // Creating a tenant is an org-admin provisioning action; gate on the same
  // `users` resource that backs the members surface in the JWT.
  const canCreateTenant = can(RESOURCES.team, "create");
  // The item and the dialog behind it go together: nothing may open a dialog
  // whose trigger is not on screen.
  const offerCreateTenant = multiTenant && canCreateTenant && tenancyCreationIncluded;

  /**
   * Move to another tenant in this organization.
   *
   * Server-side, like the org switch: the post-login Action resolves tenancy
   * from `app_metadata` and ignores `/authorize` params, so `manager.switchTenant`
   * alone re-authenticated and landed straight back on the root tenant. The
   * endpoint writes the selection into the map entry the Action reads; the
   * re-auth then picks it up.
   */
  async function switchTo(tenantId: string) {
    if (tenantId === current || busy || impersonating) return;
    setBusy(true);
    let switched = false;
    try {
      await tenancy.setActiveTenant(tenantId);
      switched = true;
      // `identityChange` runs the same teardown as a sign-out's tenant half:
      // this tenant's cached transactions and live channels must not outlast it.
      await manager.login({ returnTo: window.location.href, identityChange: "tenant-switch" });
    } catch (error) {
      // The write landed and only the reload failed (a blocked popup, a dropped
      // network): saying "you are still in the current one" would be the one
      // wrong sentence — the next sign-in lands in the NEW tenant.
      if (switched) {
        notifyError(error, {
          title: "Switched, but this tab could not reload — sign in again to land in the new tenant.",
        });
        setBusy(false);
        return;
      }
      // A failed switch used to leave the menu usable and say nothing at all,
      // so the operator stayed in the tenant they had just left — and went on
      // reading one tenant's data believing it was another's. That is the one
      // failure on this menu that can be mistaken for success.
      notifyError(error, { title: "Couldn’t switch tenant — you are still in the current one." });
      setBusy(false);
    }
  }

  /**
   * Move to another organization the caller belongs to.
   *
   * The server does the move: it verifies the membership and rewrites the home
   * tenancy in `app_metadata`. It CANNOT be done from here by asking Auth0 for a
   * different organization — the post-login Action deliberately ignores
   * `/authorize` params when resolving tenancy, so the token would come back
   * pointing at the old org. Re-authenticating afterwards is what picks up the
   * new one.
   */
  async function switchOrgTo(orgId: string) {
    if (orgId === org.orgId || busy || impersonating) return;
    setBusy(true);
    let switched = false;
    try {
      await tenancy.setActiveOrganization(orgId);
      switched = true;
      await manager.login({ returnTo: window.location.origin, identityChange: "tenant-switch" });
    } catch (error) {
      if (switched) {
        notifyError(error, {
          title:
            "Switched, but this tab could not reload — sign in again to land in the new organization.",
        });
        setBusy(false);
        return;
      }
      // Leave the menu usable rather than stranding the user mid-switch — but
      // say that nothing moved, for the same reason as `switchTo`.
      notifyError(error, {
        title: "Couldn’t switch organization — you are still in the current one.",
      });
      setBusy(false);
    }
  }

  return (
    <>
      <DropdownMenu onOpenChange={(open) => open && void loadNames()}>
        <Hint label={`Switch workspace: ${org.orgName} / ${org.tenantName}`}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="min-w-0 max-w-full gap-2 px-2" disabled={busy}
              aria-label={`Switch workspace: ${org.orgName} / ${org.tenantName}`}>
              {continent && (
                <span aria-hidden className="text-sm leading-none">
                  {CONTINENT_FLAG[continent]}
                </span>
              )}
              <span className="min-w-0 max-w-[14rem] truncate text-sm font-medium text-foreground">
                {org.orgName}
                <span className="text-muted-foreground"> / {org.tenantName}</span>
              </span>
              <ChevronsUpDown className="size-3.5 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
        </Hint>
        <DropdownMenuContent align="start" className="w-80 max-w-[calc(100vw-2rem)]">
          <DropdownMenuLabel className="flex items-center justify-between">
            <span>Current workspace</span>
            {continent && (
              <span className="text-xs font-normal text-muted-foreground" title={fullRegion?.name}>
                {CONTINENT_FLAG[continent]} {CONTINENT_SHORT_LABEL[continent]}
              </span>
            )}
          </DropdownMenuLabel>
          <div className="space-y-1 px-2 pb-2 text-sm [overflow-wrap:anywhere]">
            <div><span className="text-xs text-muted-foreground">Organization</span><div className="font-medium text-foreground">{org.orgName}</div></div>
            <div><span className="text-xs text-muted-foreground">Tenant</span><div className="font-medium text-foreground">{org.tenantName}</div></div>
            <div><span className="text-xs text-muted-foreground">Signed in as</span><div className="font-medium text-foreground">
              {session?.names.userName ?? session?.user.name ?? session?.user.email}
            </div></div>
            {currentRoles.length ? <div><span className="text-xs text-muted-foreground">Your roles here</span>
              <div className="text-foreground">{currentRoles.join(", ")}</div>
            </div> : null}
            {org.customerName && (
              <div className="text-xs text-muted-foreground">Customer {org.customerName}</div>
            )}
          </div>
          {reloading && <div role="status" className="px-2 py-1.5 text-xs text-muted-foreground">Loading workspaces…</div>}

          {orgsError != null && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground" role="alert">
                Your other organizations couldn’t be listed.{" "}
                <button
                  type="button"
                  className="underline"
                  disabled={reloading}
                  onClick={(event) => {
                    event.preventDefault();
                    void loadNames(true);
                  }}
                >
                  {reloading ? "Retrying…" : "Retry"}
                </button>
              </div>
            </>
          )}

          {myOrgs.length > 1 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Switch organization
              </DropdownMenuLabel>
              {myOrgs.map((o) => (
                <DropdownMenuItem
                  key={o.orgId}
                  disabled={busy || impersonating || o.isCurrent}
                  onSelect={(e) => {
                    e.preventDefault();
                    void switchOrgTo(o.orgId);
                  }}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{o.name}</span>
                  {o.isCurrent && <Check className="size-3.5 shrink-0" />}
                </DropdownMenuItem>
              ))}
              {/* Greyed out with no reason read as broken, not as unavailable. */}
              {impersonating && !listsTenants ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  Switching needs a fresh sign-in, which an impersonated session cannot do.
                </p>
              ) : null}
            </>
          )}

          {/* The tenant section renders whenever the list is KNOWN to hold more
              than one — and whenever it is not known at all. An empty list
              after a failed read is not evidence of a single-tenant org, and
              drawing nothing was the most confident possible statement of the
              one thing this menu could not check. */}
          {tenantsError != null ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Tenants
              </DropdownMenuLabel>
              <div className="px-2 py-1.5 text-xs text-muted-foreground" role="alert">
                This organization’s tenants couldn’t be listed, so any others you can reach
                aren’t shown.{" "}
                <button
                  type="button"
                  className="underline"
                  disabled={reloading}
                  onClick={(event) => {
                    event.preventDefault();
                    void loadNames(true);
                  }}
                >
                  {reloading ? "Retrying…" : "Retry"}
                </button>
              </div>
              <DropdownMenuItem disabled>
                <Check className="size-4" />
                <span className="truncate">{org.tenantName}</span>
              </DropdownMenuItem>
            </>
          ) : listsTenants ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Tenants</DropdownMenuLabel>
              {tenants.map((t) => (
                <DropdownMenuItem
                  key={t}
                  onSelect={() => void switchTo(t)}
                  disabled={busy || impersonating}
                >
                  {t === current ? <Check className="size-4" /> : <span className="w-4" />}
                  <span className="truncate">{tenantLabel(t)}</span>
                  {rolesIn(t) ? (
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">{rolesIn(t)}</span>
                  ) : null}
                </DropdownMenuItem>
              ))}
              {/* Listed but not offered: the tenants are worth SEEING while
                  supporting somebody (which tenant is this ticket about?), and
                  switching is the one thing this session cannot finish. */}
              {impersonating ? (
                <p className="px-2 py-1.5 text-xs text-muted-foreground">
                  Switching needs a fresh sign-in, which an impersonated session cannot do.
                </p>
              ) : null}
            </>
          ) : null}

          {tenantsError == null && hiddenTenants > 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {hiddenTenants === 1
                ? "1 other tenant in this organization — you hold no role there."
                : `${hiddenTenants} other tenants in this organization — you hold no role there.`}
            </p>
          ) : null}

          {offerCreateTenant && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
                <Plus className="size-4" />
                Create tenant
              </DropdownMenuItem>
            </>
          )}

          <DropdownMenuSeparator />
          {multiTenant && <DropdownMenuItem asChild>
            <Link to={ROUTES.settingsStructure} search={{ view: "access" }}>
              <Network className="size-4" />
              People &amp; tenant access
            </Link>
          </DropdownMenuItem>}
          {canCreateTenant && <DropdownMenuItem asChild>
            <Link to={ROUTES.team}>
              <UserPlus className="size-4" />
              Invite people
            </Link>
          </DropdownMenuItem>}
          <DropdownMenuItem asChild>
            <Link to={ROUTES.team}>
              <Users className="size-4" />
              Members &amp; team
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            {/* The tenancy page itself, not the top of Settings: this menu is
                where somebody stands when they want to rename or add a tenant.
                Falls back to the Settings index when tenancy is not part of the
                plan, because that page is then not in the rail either. */}
            <Link to={multiTenant ? ROUTES.settingsWorkspace : ROUTES.settings}>
              <SettingsIcon className="size-4" />
              Workspace settings
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Kept mounted while open: a plan refusal inside it re-reads the plan,
          which withdraws the offer while the dialog is still saying why. */}
      {(offerCreateTenant || createOpen) && (
        <CreateTenantDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          orgId={org.orgId}
          onCreated={(tenantId) => {
            if (tenantId) void manager.switchTenant(tenantId, () => window.location.reload());
            else window.location.reload();
          }}
        />
      )}
    </>
  );
}
